import {
    useMemo, useCallback,
} from 'react'
import useLabel from 'app/hooks/useLabel'
import Card from 'app/shared-components/Card'
import SimpleTable, {
    RowComponent,
} from 'app/shared-components/SimpleTable'
import {
    Column,
} from 'app/types/simpletable.types'
import useEnumValues from 'app/hooks/useEnumValues'
import useValidation from 'app/hooks/useValidation'
import useAccountManagementRoutes from 'app/SkyNetRoutes/hooks/useAccountManagementRoutes'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import Checkbox from 'app/shared-components/Checkbox'
import getColumns, {
    QuotationOptionsTableType,
} from './QuotationOptionsTable.config'
import useStyles from './QuotationOptionsTable.styles'

export default function QuotationOptionsTable({
    data, onChange, renderButtonPanel, toggleAll,
}: Readonly<{
    data: QuotationOptionsTableType[],
    onChange: (...args: any[]) => void,
    renderButtonPanel: ({
        isValid, disabled,
    }: { isValid: boolean, disabled: boolean }) => JSX.Element,
    toggleAll: (value: boolean) => void
}>) {
    const l = useLabel()
    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()
    const {
        Lanes: LanesRoutes,
    } = useAccountManagementRoutes()

    const createLaneLink = useCallback((id: number) => {
        return id ? generateSkyNetLink({
            domainPath: LanesRoutes.Overview.path,
            params: {
                key: id,
            },
        }) : null
    }, [LanesRoutes.Overview.path])

    const isSelectedAll = useMemo(() => {
        return data.every(({
            selected,
        }) => {
            return selected
        })
    }, [data])

    const isDisabledAll = useMemo(() => {
        return data.every(({
            requestedLaneId,
        }) => {
            return Boolean(requestedLaneId)
        })
    }, [data])

    const onSelectAllClick = useCallback(() => {
        if (isDisabledAll) return
        if (isSelectedAll) {
            toggleAll(false)
            return
        }

        toggleAll(true)
    }, [
        toggleAll,
        isSelectedAll,
        isDisabledAll,
    ])

    const columns = useMemo((): Column<keyof QuotationOptionsTableType>[] => {
        return getColumns({
            classes,
            l,
            handleChange: onChange,
            getAllowedValues,
            createLaneLink,
            selectAll: (
                <Checkbox
                    value={isSelectedAll}
                    disabled={isDisabledAll}
                    onChange={onSelectAllClick}
                />
            ),
        })
    }, [
        classes,
        l,
        onChange,
        getAllowedValues,
        createLaneLink,
        isSelectedAll,
        isDisabledAll,
        onSelectAllClick,
    ])

    const selectedItems = useMemo(() => {
        return data.filter(({
            selected,
        }) => {
            return selected
        })
    }, [data])

    const {
        isValid,
    } = useValidation({
        fields: columns,
        values: selectedItems,
    })

    return (
        <>
            <Card title="Quotation Options">
                <SimpleTable
                    name="QuotationOptionsTable"
                    columns={columns}
                    data={data}
                    component={RowComponent}
                    onChange={onChange}
                    disabled
                    classNames={{
                        tableCellHead: classes.tableCellHead,
                        tableCell: classes.tableCellHead,
                    }}
                />
            </Card>
            {renderButtonPanel({
                isValid: selectedItems.length && isValid,
                disabled: isDisabledAll,
            })}
        </>

    )
}
